import React from 'react';

import { RequestExceptionWithResponse } from '@eika-infrastruktur/request';
import { Button } from '@eika/button';
import { CheckmarkAnimation, CheckmarkAnimationTypes } from '@eika/checkmark-animation';
import { Icon, IconTypes } from '@eika/icon';

import { useConfig } from '../../../defaultConfig';
import type { AccountId, ApprovalStatus } from '../../../generated-types';
import useTranslation from '../../../hooks/useTranslation';
import CenteredModalContent from '../../shared/CenteredModalContent/CenteredModalContent';
import { ErrorCardWrapper } from '../../shared/ErrorCardWrapper';

export interface AvtalegiroUpdateInfoProps {
    error?: RequestExceptionWithResponse;
    status?: ApprovalStatus;
    retry: () => void;
    onClose: () => void;
    fromAccountId?: AccountId;
    isDeleteOperation?: boolean;
}

const AvtalegiroUpdateInfo: React.FC<AvtalegiroUpdateInfoProps> = ({
    error,
    status,
    retry,
    onClose,
    fromAccountId,
    isDeleteOperation,
}) => {
    const translated = useTranslation();
    const { avtalegiroLink } = useConfig();

    const checkAvtalegiro = () => {
        onClose();
        if (fromAccountId) {
            window.location.href = avtalegiroLink!.url;
        }
    };

    if (error) {
        return (
            <CenteredModalContent>
                <ErrorCardWrapper exception={error} isModal>
                    <Button onClick={retry}>{translated.ok}</Button>
                </ErrorCardWrapper>
            </CenteredModalContent>
        );
    }
    if (isDeleteOperation) {
        switch (status) {
            case 'UNKNOWN':
            case 'REJECTED':
                return (
                    <CenteredModalContent withMessage>
                        <Icon className="wallet-questionmark" type={IconTypes.WALLET_QUESTIONMARK} />
                        <h3 className="typography-h3">
                            {translated.avtaleGiroAgreement.deleteAvtaleGiroInfoMessages.error.heading}
                        </h3>
                        <p>{translated.avtaleGiroAgreement.deleteAvtaleGiroInfoMessages.error.description}</p>
                        {fromAccountId && (
                            <Button onClick={checkAvtalegiro}>
                                {translated.avtaleGiroAgreement.deleteAvtaleGiroInfoMessages.error.check}
                            </Button>
                        )}
                    </CenteredModalContent>
                );
            default:
                return (
                    <CenteredModalContent withMessage>
                        <CheckmarkAnimation checkmarkAnimationType={CheckmarkAnimationTypes.POSITIVE} />
                        <h3 className="typography-h3">
                            {translated.avtaleGiroAgreement.deleteAvtaleGiroInfoMessages.success}
                        </h3>
                    </CenteredModalContent>
                );
        }
    }
    switch (status) {
        case 'UNKNOWN':
        case 'REJECTED':
            return (
                <CenteredModalContent withMessage>
                    <Icon className="wallet-questionmark" type={IconTypes.WALLET_QUESTIONMARK} />
                    <h3 className="typography-h3">{translated.avtalegiroValidation.error.heading}</h3>
                    <p>{translated.avtalegiroValidation.error.description}</p>
                    {fromAccountId && (
                        <Button onClick={checkAvtalegiro}>{translated.avtalegiroValidation.error.check}</Button>
                    )}
                </CenteredModalContent>
            );
        default:
            return (
                <CenteredModalContent withMessage>
                    <CheckmarkAnimation checkmarkAnimationType={CheckmarkAnimationTypes.POSITIVE} />
                    <h3 className="typography-h3">{translated.avtalegiroValidation.updateSuccess}</h3>
                </CenteredModalContent>
            );
    }
};

export default AvtalegiroUpdateInfo;
